import { CollectionHelper } from 'src/app/helpers/collection-helper';
import { GeneralHelper } from 'src/app/helpers/general-helper';
import ProjectHelper from 'src/app/helpers/project-helper';
import { ImmoTask } from 'src/app/model/immo-task/immo-task';
import Product from 'src/app/model/product/product';
import { ProjectPlacement } from 'src/app/model/project/projectPlacement';
import {
  SubProject,
  SUB_PROJECT_TYPES
} from 'src/app/model/project/sub-project';
import RegisteredAction from 'src/app/model/registered-action';
import { ImmoIPImmoTaskChangesObject } from 'src/app/services/immo-ip-placer-offline-changes/immo-ip-immo-task-changes/immo-ip-immo-task-changes-object';
import { ImmoIPImmoTaskChangesService } from 'src/app/services/immo-ip-placer-offline-changes/immo-ip-immo-task-changes/immo-ip-immo-task-changes.service';
import { ImmoIPProductChangesObject } from 'src/app/services/immo-ip-placer-offline-changes/immo-ip-product-changes/immo-ip-product-changes-object';
import { ImmoIPProductChangesService } from 'src/app/services/immo-ip-placer-offline-changes/immo-ip-product-changes/immo-ip-product-changes.service';
import { ImmoIPSubProjectChangesObject } from 'src/app/services/immo-ip-placer-offline-changes/immo-ip-sub-project-changes/immo-ip-sub-project-changes-object';
import { ImmoIPSubProjectChangesService } from 'src/app/services/immo-ip-placer-offline-changes/immo-ip-sub-project-changes/immo-ip-sub-project-changes.service';
import { OfflineStorageTourModifiedConfigHelper } from './offline-storage-tour-modified-config-helper';

export class OfflineStorageTourHelper {
  private constructor() {}

  static async applySubProjectChanges(
    subProjects: SubProject[],
    immoIPSubProjectChangesService: ImmoIPSubProjectChangesService,
    immoIPProductChangesService: ImmoIPProductChangesService
  ): Promise<void> {
    if (!CollectionHelper.isPopulated(subProjects)) {
      return;
    }

    const subProjectIds = subProjects.map(s => (s == null ? null : s.id));
    CollectionHelper.filterInPlace(subProjectIds, id => id != null);

    const subProjectChangesObjects = await immoIPSubProjectChangesService.getObjects(
      subProjectIds
    );

    const subProjectChangesMap = CollectionHelper.isPopulated(
      subProjectChangesObjects
    )
      ? new Map<number, ImmoIPSubProjectChangesObject>(
          subProjectChangesObjects.map(o => [o.id, o])
        )
      : null;
    for (const sp of subProjects) {
      if (
        sp.type === SUB_PROJECT_TYPES.BASE_PROJECT &&
        sp.project != null &&
        CollectionHelper.isPopulated(sp.project.products)
      ) {
        await this.applyProductChanges(
          sp.project.products,
          immoIPProductChangesService
        );
      }
      const changeObject =
        subProjectChangesMap == null ? null : subProjectChangesMap.get(sp.id);
      if (
        changeObject != null &&
        (sp.tourItem == null || sp.tourItem.id === changeObject.tourItemId)
      ) {
        this.applySubProjectChange(sp, changeObject);
      } else {
        continue;
      }
      if (!ProjectHelper.EXECUTED_FOR_CLIENT_STATUSES.includes(sp.status)) {
        const executionState = this.getSubProjectExecutionState(sp);
        if (sp.type === SUB_PROJECT_TYPES.BASE_PROJECT) {
          if (
            sp.project != null &&
            CollectionHelper.isPopulated(sp.project.products)
          ) {
            sp.project.products.forEach(p => {
              if (p.productPlacement != null) {
                p.productPlacement.executionComplete = executionState;
              }
            });
          }
        } else {
          if (sp.projectPlacement != null) {
            sp.projectPlacement.placementDone = executionState;
          }
        }
      }
    }
  }

  private static applySubProjectChange(
    subProject: SubProject,
    changeObject: ImmoIPSubProjectChangesObject
  ): void {
    if (subProject.projectPlacement != null) {
      if (GeneralHelper.hasOwnProperty(changeObject, 'placerFeedback')) {
        subProject.projectPlacement.placerFeedback =
          changeObject.placerFeedback;
      }
      if (CollectionHelper.isPopulated(changeObject.registeredActions)) {
        this.setRegisteredActions(
          subProject.projectPlacement,
          changeObject.registeredActions
        );
      }
      if (GeneralHelper.hasOwnProperty(changeObject, 'placementDone')) {
        subProject.projectPlacement.placementDone = changeObject.placementDone;
      }
    }
    if (GeneralHelper.hasOwnProperty(changeObject, 'currentExecutionDate')) {
      subProject.currentExecutionDate = changeObject.currentExecutionDate;
    }
    if (GeneralHelper.hasOwnProperty(changeObject, 'internalInfo')) {
      subProject.internalInfo = changeObject.internalInfo;
    }
    if (GeneralHelper.hasOwnProperty(changeObject, 'recurringInternalInfo')) {
      subProject.recurringInternalInfo = changeObject.recurringInternalInfo;
    }

    OfflineStorageTourModifiedConfigHelper.applyModifiedConfig(
      subProject,
      changeObject
    );
  }

  private static setRegisteredActions(
    projectPlacement: ProjectPlacement,
    registeredActions: RegisteredAction[]
  ): void {
    if (projectPlacement.registeredActions == null) {
      projectPlacement.registeredActions = [];
    }
    projectPlacement.registeredActions.push(...registeredActions);
    projectPlacement.registeredActions.sort(
      GeneralHelper.compareRegisteredActionsDesc
    );
  }

  private static async applyProductChanges(
    products: Product[],
    immoIPProductChangesService: ImmoIPProductChangesService
  ): Promise<void> {
    const productIds = products.map(p => (p == null ? null : p.id));
    CollectionHelper.filterInPlace(productIds, id => id != null);

    const productChangesObject = await immoIPProductChangesService.getObjects(
      productIds
    );
    if (!CollectionHelper.isPopulated(productChangesObject)) {
      return;
    }

    const productChangesMap = new Map<number, ImmoIPProductChangesObject>(
      productChangesObject.map(o => [o.id, o])
    );

    for (const p of products) {
      const changeObject = productChangesMap.get(p.id);
      if (changeObject != null) {
        this.applyProductChange(p, changeObject);
      }
    }
  }

  private static applyProductChange(
    product: Product,
    changeObject: ImmoIPProductChangesObject
  ): void {
    if (product.productPlacement != null) {
      if (GeneralHelper.hasOwnProperty(changeObject, 'installerFeedback')) {
        product.productPlacement.installerFeedback =
          changeObject.installerFeedback;
      }
      if (GeneralHelper.hasOwnProperty(changeObject, 'executionComplete')) {
        product.productPlacement.executionComplete =
          changeObject.executionComplete;
      }
      if (GeneralHelper.hasOwnProperty(changeObject, 'executionDate')) {
        product.productPlacement.executionDate = changeObject.executionDate;
      }
      if (GeneralHelper.hasOwnProperty(changeObject, 'executionTime')) {
        product.productPlacement.executionTime = <any>(
          changeObject.executionTime
        );
      }
    }
  }

  static getSubProjectExecutionState(subProject: SubProject): boolean {
    if (
      ProjectHelper.EXECUTED_FOR_CLIENT_STATUSES.includes(subProject.status)
    ) {
      return true;
    }
    if (subProject.type === SUB_PROJECT_TYPES.BASE_PROJECT) {
      if (
        subProject.project != null &&
        CollectionHelper.isPopulated(subProject.project.products)
      ) {
        let intermediate: boolean = null;
        for (const product of subProject.project.products) {
          if (product.productPlacement != null) {
            intermediate = product.productPlacement.executionComplete;
            if (intermediate) {
              return true;
            } else if (intermediate === false) {
              return false;
            }
          }
        }
        return intermediate;
      }
    } else {
      if (subProject.projectPlacement != null) {
        if (subProject.projectPlacement.placementDone) {
          return true;
        }
        return subProject.projectPlacement.placementDone === false
          ? false
          : null;
      }
    }
  }

  static async applyImmoTaskChanges(
    immoTasks: ImmoTask[],
    immoIPImmoTaskChangesService: ImmoIPImmoTaskChangesService
  ): Promise<void> {
    if (!CollectionHelper.isPopulated(immoTasks)) {
      return;
    }

    const immoTaskIds = immoTasks.map(t => (t == null ? null : t.id));
    CollectionHelper.filterInPlace(immoTaskIds, id => id != null);

    const immoTaskChangesObjects = await immoIPImmoTaskChangesService.getObjects(
      immoTaskIds
    );

    if (!CollectionHelper.isPopulated(immoTaskChangesObjects)) {
      return;
    }

    const immoTaskChangesMap = new Map<number, ImmoIPImmoTaskChangesObject>(
      immoTaskChangesObjects.map(o => [o.id, o])
    );

    for (const task of immoTasks) {
      const changeObject = immoTaskChangesMap.get(task.id);
      if (
        changeObject != null &&
        (task.tourItem == null || task.tourItem.id === changeObject.tourItemId)
      ) {
        this.applyImmoTaskChange(task, changeObject);
      }
    }
  }

  private static applyImmoTaskChange(
    immoTask: ImmoTask,
    changeObject: ImmoIPImmoTaskChangesObject
  ): void {
    if (GeneralHelper.hasOwnProperty(immoTask, 'executionFeedback')) {
      immoTask.executionFeedback = changeObject.executionFeedback;
    }
    if (GeneralHelper.hasOwnProperty(immoTask, 'executed')) {
      immoTask.executed = changeObject.executed;
    }
    if (GeneralHelper.hasOwnProperty(immoTask, 'executedRegistration')) {
      immoTask.executedRegistration = changeObject.executedRegistration;
    }
  }
}
